import { useMemo, useState } from "react";
import { Copy, Loader2, AlertTriangle, PackageOpen } from "lucide-react";
import { api, formatMoney } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = [
  { value: "paid", label: "Paid", cls: "bg-blue-100 text-blue-700" },
  { value: "ordered", label: "Ordered from supplier", cls: "bg-amber-100 text-amber-700" },
  { value: "shipped", label: "Shipped", cls: "bg-purple-100 text-purple-700" },
  { value: "delivered", label: "Delivered", cls: "bg-green-100 text-green-700" },
  { value: "problem", label: "Problem", cls: "bg-red-100 text-red-700" },
  { value: "refunded", label: "Refunded", cls: "bg-gray-200 text-gray-600" },
];
const statusOf = (o) => STATUSES.find((s) => s.value === o.fulfillment_status);

const daysSince = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 86400000 : 0);
const stripeFee = (o) => o.total * 0.029 + 0.3;
const profitOf = (o) =>
  o.supplier_cost == null ? null : o.total - o.supplier_cost - (o.shipping_cost || 0) - stripeFee(o);

const delayOf = (o) => {
  if (o.fulfillment_status === "paid" && daysSince(o.paid_at || o.created_at) > 2)
    return `Waiting ${Math.floor(daysSince(o.paid_at || o.created_at))}d — order from supplier`;
  if (o.fulfillment_status === "shipped" && daysSince(o.shipped_at || o.created_at) > 21)
    return `In transit ${Math.floor(daysSince(o.shipped_at || o.created_at))}d — check tracking`;
  return null;
};

const StatusBadge = ({ order }) => {
  const s = statusOf(order);
  if (!s)
    return <span className="text-[11px] font-bold uppercase px-2 py-1 rounded bg-gray-100 text-gray-500" data-testid={`status-badge-${order.id}`}>Unpaid</span>;
  return (
    <span className={`text-[11px] font-bold uppercase px-2 py-1 rounded whitespace-nowrap ${s.cls}`} data-testid={`status-badge-${order.id}`}>
      {s.label}
    </span>
  );
};

const orderCopyBlock = (o) => {
  const c = o.customer || {};
  const items = (o.items || [])
    .map(
      (li, i) =>
        `${i + 1}. ${li.name}${li.size ? ` — Size: ${li.size}` : ""}${li.design ? ` — Colour/Design: ${li.design}` : ""}${li.personalization ? ` — Print name: ${li.personalization}` : ""} — Qty: ${li.quantity}`
    )
    .join("\n");
  return `ORDER ${o.id.slice(0, 8).toUpperCase()}\n\nSHIP TO:\n${c.name}\n${c.address}\n${c.city} ${c.postcode}\n${o.country_name || o.country}\n${c.email}\n\nITEMS:\n${items}`;
};

function SupplierForm({ order, onSaved }) {
  const [f, setF] = useState({
    supplier_order_no: order.supplier_order_no || "",
    supplier_tracking: order.supplier_tracking || "",
    carrier: order.carrier || "",
    date_ordered: order.date_ordered || "",
    supplier_cost: order.supplier_cost ?? "",
    shipping_cost: order.shipping_cost ?? "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        supplier_order_no: f.supplier_order_no || null,
        supplier_tracking: f.supplier_tracking || null,
        carrier: f.carrier || null,
        date_ordered: f.date_ordered || null,
        supplier_cost: f.supplier_cost === "" ? null : parseFloat(f.supplier_cost),
        shipping_cost: f.shipping_cost === "" ? null : parseFloat(f.shipping_cost),
      };
      const { data } = await api.put(`/admin/orders/${order.id}/supplier`, body);
      toast.success(data.tracking_email_sent ? "Saved — tracking email sent to the customer ✉️" : "Supplier details saved");
      onSaved(data.order);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save supplier details");
    } finally {
      setSaving(false);
    }
  };

  const input = (key, label, props = {}) => (
    <div>
      <label className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</label>
      <input
        value={f[key]}
        onChange={(e) => setF({ ...f, [key]: e.target.value })}
        data-testid={`supplier-${key.replace(/_/g, "-")}`}
        className="w-full border border-ink/20 focus:border-brand outline-none rounded-md p-2 mt-0.5 text-sm"
        {...props}
      />
    </div>
  );

  return (
    <div>
      <p className="overline mb-2">3 · Supplier details</p>
      <div className="grid grid-cols-2 gap-3">
        {input("supplier_order_no", "Supplier order #")}
        {input("date_ordered", "Date ordered", { type: "date" })}
        {input("supplier_tracking", "Tracking number")}
        {input("carrier", "Carrier", { placeholder: "e.g. AliExpress Standard" })}
        {input("supplier_cost", "Supplier cost (AU$)", { inputMode: "decimal", placeholder: "0.00" })}
        {input("shipping_cost", "Shipping cost (AU$)", { inputMode: "decimal", placeholder: "0.00" })}
      </div>
      <button onClick={save} disabled={saving} className="btn-primary w-full !py-2 mt-3 disabled:opacity-50" data-testid="supplier-save-btn">
        {saving ? <Loader2 size={15} className="animate-spin" /> : "Save supplier details"}
      </button>
      <p className="text-[11px] text-muted-foreground mt-1.5">
        {order.tracking_email_sent
          ? "✓ Tracking email already sent to the customer (sent once only)."
          : "Saving a tracking number emails the customer once and marks the order Shipped."}
      </p>
    </div>
  );
}

function ProfitBox({ order }) {
  const profit = profitOf(order);
  const row = (label, val, cls = "") => (
    <div className={`flex justify-between ${cls}`}><span>{label}</span><span className="font-mono">{val}</span></div>
  );
  return (
    <div className="bg-secondary rounded-md p-3 text-xs space-y-1" data-testid="profit-box">
      <p className="overline !text-[10px] mb-1">Profit on this order</p>
      {row("Sale total", formatMoney(order.total, order.currency))}
      {row("Supplier cost", order.supplier_cost == null ? "— enter above" : `−${formatMoney(order.supplier_cost, order.currency)}`)}
      {row("Shipping cost", order.shipping_cost == null ? "—" : `−${formatMoney(order.shipping_cost, order.currency)}`)}
      {row("Stripe fee (est. 2.9% + 0.30)", `−${formatMoney(stripeFee(order), order.currency)}`)}
      {row("Profit", profit == null ? "add supplier cost" : formatMoney(profit, order.currency),
        `border-t border-ink/10 pt-1 font-bold ${profit != null && profit < 0 ? "text-red-600" : "text-green-700"}`)}
    </div>
  );
}

export default function OrdersPanel({ orders, products, reload }) {
  const [filter, setFilter] = useState("needs_action");
  const [open, setOpen] = useState(null);

  const supplierLink = (pid) => products.find((p) => p.id === pid)?.source_url || "";

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied — ready to paste`);
  };

  const needsAction = useMemo(
    () =>
      orders
        .filter((o) => o.payment_status === "paid" && o.fulfillment_status === "paid")
        .sort((a, b) => (a.paid_at || a.created_at).localeCompare(b.paid_at || b.created_at)),
    [orders]
  );

  const weeklyProfit = useMemo(() => {
    const paid = orders.filter((o) => o.payment_status === "paid" && daysSince(o.created_at) <= 7);
    const withCosts = paid.filter((o) => o.supplier_cost != null);
    return {
      total: withCosts.reduce((s, o) => s + profitOf(o), 0),
      counted: withCosts.length,
      missing: paid.length - withCosts.length,
      currency: paid[0]?.currency || "nzd",
    };
  }, [orders]);

  const shown = useMemo(() => {
    if (filter === "needs_action") return needsAction;
    if (filter === "all") return orders;
    return orders.filter((o) => o.fulfillment_status === filter);
  }, [filter, orders, needsAction]);

  const pill = (value, label, count, warn = false) => (
    <button
      key={value}
      onClick={() => setFilter(value)}
      data-testid={`filter-${value}`}
      className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border transition-colors ${
        filter === value ? "bg-ink text-white border-ink" : warn && count > 0 ? "border-brand text-brand hover:bg-brand/5" : "border-ink/20 hover:border-ink"
      }`}
    >
      {label}{count != null ? ` (${count})` : ""}
    </button>
  );

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/fulfillment`, { fulfillment_status: status });
      toast.success(`Status set to ${STATUSES.find((s) => s.value === status)?.label}`);
      reload();
      setOpen((prev) => (prev && prev.id === id ? { ...prev, fulfillment_status: status } : prev));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update status");
    }
  };

  const multiItem = open && (open.items || []).reduce((s, li) => s + li.quantity, 0) > 1;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-xl font-bold">Orders</h2>
        <div className="bg-white border border-ink/10 rounded-md px-4 py-2 text-sm" data-testid="weekly-profit">
          <span className="text-muted-foreground text-xs uppercase tracking-wide mr-2">Profit this week</span>
          <span className="font-mono font-bold">{formatMoney(weeklyProfit.total, weeklyProfit.currency)}</span>
          {weeklyProfit.missing > 0 && (
            <span className="text-[11px] text-amber-600 ml-2">({weeklyProfit.missing} order{weeklyProfit.missing > 1 ? "s" : ""} missing costs)</span>
          )}
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
        {pill("needs_action", "⚡ Needs action", needsAction.length, true)}
        {pill("all", "All", orders.length)}
        {STATUSES.map((s) => pill(s.value, s.label, orders.filter((o) => o.fulfillment_status === s.value).length))}
      </div>

      {filter === "needs_action" && (
        <p className="text-xs text-muted-foreground mb-3">Paid orders you haven't ordered from the supplier yet — oldest first so nothing gets missed.</p>
      )}

      <div className="bg-white border border-ink/10 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left"><tr>
            <th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Total</th>
            <th className="p-3">Status</th><th className="p-3">Profit</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {shown.map((o) => {
              const delay = delayOf(o);
              const profit = profitOf(o);
              return (
                <tr key={o.id} className={`border-t border-ink/5 ${delay ? "bg-red-50" : ""}`} data-testid={`admin-order-${o.id}`}>
                  <td className="p-3 font-mono text-xs">{o.id.slice(0, 8)}…<br /><span className="text-muted-foreground">{(o.created_at || "").slice(0, 10)}</span></td>
                  <td className="p-3">{o.customer?.name}<br /><span className="text-xs text-muted-foreground">{o.country}</span></td>
                  <td className="p-3 font-mono">{formatMoney(o.total, o.currency)}</td>
                  <td className="p-3">
                    <StatusBadge order={o} />
                    {delay && (
                      <p className="text-[11px] text-red-600 font-bold mt-1 flex items-center gap-1" data-testid={`delay-flag-${o.id}`}>
                        <AlertTriangle size={11} /> {delay}
                      </p>
                    )}
                  </td>
                  <td className="p-3 font-mono text-xs">{profit == null ? "—" : <span className={profit < 0 ? "text-red-600" : "text-green-700"}>{formatMoney(profit, o.currency)}</span>}</td>
                  <td className="p-3">
                    {statusOf(o) && (
                      <button onClick={() => setOpen(o)} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`fulfil-btn-${o.id}`}>Open</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                {filter === "needs_action" ? "Nothing needs action — all paid orders have been sent to the supplier. 🎉" : "No orders here."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white" aria-describedby={undefined} data-testid="fulfil-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              Order {open?.id?.slice(0, 8).toUpperCase()} {open && <StatusBadge order={open} />}
            </DialogTitle>
          </DialogHeader>
          {open && (
            <div className="space-y-5 text-sm">
              {multiItem && (
                <p className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800 flex items-center gap-2" data-testid="parcels-note">
                  <PackageOpen size={14} /> Multiple items — they may arrive in separate parcels.
                </p>
              )}

              <div>
                <p className="overline mb-2">1 · Order these from the supplier</p>
                <div className="space-y-2">
                  {open.items?.map((li, i) => (
                    <div key={`${li.product_id}-${li.size || i}`} className="flex items-center justify-between border border-ink/10 rounded-md p-3" data-testid={`fulfil-item-${i}`}>
                      <div>
                        <p className="font-medium">{li.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {li.size ? `Size ${li.size} · ` : ""}{li.design ? `Design: ${li.design} · ` : ""}{li.personalization ? `Print name: ${li.personalization} · ` : ""}Qty {li.quantity} · {formatMoney(li.price, open.currency)}
                        </p>
                      </div>
                      {supplierLink(li.product_id) ? (
                        <a href={supplierLink(li.product_id)} target="_blank" rel="noreferrer"
                          className="btn-primary !py-1.5 !px-3 text-xs whitespace-nowrap" data-testid={`fulfil-supplier-link-${i}`}>
                          AliExpress ↗
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">no supplier link</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="overline mb-2">2 · Copy the order details</p>
                <div className="bg-secondary rounded-md p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap" data-testid="fulfil-address">
                  {orderCopyBlock(open)}
                </div>
                <button
                  onClick={() => copyText(orderCopyBlock(open), "Order details")}
                  className="mt-2 w-full border border-ink hover:bg-ink hover:text-white transition-colors rounded-md py-2 px-3 text-xs font-bold flex items-center justify-center gap-2"
                  data-testid="copy-order-btn"
                >
                  <Copy size={13} /> Copy order details (paste at supplier checkout)
                </button>
              </div>

              <SupplierForm key={open.id} order={open} onSaved={(o) => { setOpen(o); reload(); }} />

              <ProfitBox order={open} />

              <div>
                <p className="overline mb-2">Status</p>
                <Select value={open.fulfillment_status} onValueChange={(v) => updateStatus(open.id, v)}>
                  <SelectTrigger className="w-full h-9 text-sm" data-testid="order-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
